import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import {
  MessageSquare, Users, Phone, Mail, Search, RefreshCw, ExternalLink,
  Loader2, CheckCircle2, AlertCircle, Settings, BarChart3, Send,
  UserPlus, MessageCircle, Target, Trophy, XCircle, Building2,
  TrendingUp, DollarSign, Clock, Zap
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

// Lead stage configuration
const LEAD_STAGES = [
  { id: 'new_lead', label: 'New Lead', color: 'bg-blue-100 text-blue-800', icon: UserPlus },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800', icon: MessageCircle },
  { id: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-800', icon: Target },
  { id: 'closed_won', label: 'Won', color: 'bg-green-100 text-green-800', icon: Trophy },
  { id: 'closed_lost', label: 'Lost', color: 'bg-red-100 text-red-800', icon: XCircle }
];

const CHATWOOT_BASE_URL = 'https://giri-u64310.vm.elestio.app';
const CHATWOOT_ACCOUNT_ID = '1';

const AdminCommunicationHub = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('inbox');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [leads, setLeads] = useState([]);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [provisioningAll, setProvisioningAll] = useState(false);
  const [adminChatwootConfig, setAdminChatwootConfig] = useState(null);

  useEffect(() => {
    fetchData();
    fetchAdminChatwootConfig();
  }, []);

  const fetchAdminChatwootConfig = async () => {
    try {
      const data = await apiCall('/api/chatwoot/admin/config');
      setAdminChatwootConfig(data);
    } catch (e) {
      console.error('Failed to get admin Chatwoot config:', e);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Chatwoot stats
      const statsData = await apiCall('/api/chatwoot/admin/stats');
      setStats(statsData);
      
      // Fetch vendors with Chatwoot status - use admin/detailed endpoint
      const vendorsData = await apiCall('/api/vendors/admin/detailed');
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      
      // Fetch all leads (admin endpoint)
      const leadsData = await apiCall('/api/chatwoot/admin/leads');
      setLeads(leadsData.leads || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const provisionAllVendors = async () => {
    setProvisioningAll(true);
    try {
      const result = await apiCall('/api/chatwoot/admin/provision-all', { method: 'POST' });
      toast({
        title: 'Provisioning Complete',
        description: `Processed ${result.total} vendors`
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to provision vendors',
        variant: 'destructive'
      });
    } finally {
      setProvisioningAll(false);
    }
  };

  const getProvisionedVendors = () => {
    return vendors.filter(v => v.chatwoot?.provisioned);
  };

  // iter-355 — a vendor provisioned under the OLD dedicated-inbox model
  // (has chatwoot.provisioned=true but never joined the central WABA
  // inbox) still needs backfilling — mirrors the backend's
  // `/admin/provision-all` query so the button's count/list stays in sync.
  const getUnprovisionedVendors = () => {
    const centralInboxId = stats?.central_waba_inbox_id;
    return vendors.filter(v => !v.chatwoot?.provisioned || (centralInboxId != null && v.chatwoot?.inbox_id !== centralInboxId));
  };

  // Admin SSO URL - use SSO if available, fallback to direct URL
  const adminDashboardUrl = adminChatwootConfig?.sso_url || `${CHATWOOT_BASE_URL}/app/accounts/${CHATWOOT_ACCOUNT_ID}/dashboard`;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[600px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-green-600" />
              Communication Hub
            </h1>
            <p className="text-muted-foreground">
              Manage WhatsApp messaging, vendor inboxes, and customer leads
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <a href={adminDashboardUrl} target="_blank" rel="noopener noreferrer">
              <Button>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Chatwoot
              </Button>
            </a>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Provisioned Vendors</p>
                  <p className="text-2xl font-bold">{stats?.provisioned_vendors || 0}</p>
                </div>
                <Building2 className="h-8 w-8 text-green-600 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-2xl font-bold">{stats?.total_leads || 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-600 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Webhook Events</p>
                  <p className="text-2xl font-bold">{stats?.total_webhook_events || 0}</p>
                </div>
                <Zap className="h-8 w-8 text-amber-600 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Closed Won</p>
                  <p className="text-2xl font-bold">{stats?.leads_by_stage?.closed_won || 0}</p>
                </div>
                <Trophy className="h-8 w-8 text-emerald-600 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="inbox" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Admin Inbox
            </TabsTrigger>
            <TabsTrigger value="vendors" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Vendor Inboxes
              <Badge variant="secondary" className="ml-1">{vendors.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="leads" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              All Leads
              <Badge variant="secondary" className="ml-1">{leads.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Admin Inbox Tab - Embedded Chatwoot */}
          <TabsContent value="inbox" className="mt-4">
            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Admin Support Inbox</CardTitle>
                <CardDescription>
                  Handle unassigned customer inquiries and support requests
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {/* Info banner about iframe limitation */}
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-800 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>For the best experience, open Chatwoot in a new tab</span>
                  </div>
                  <a href={adminDashboardUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Chatwoot
                    </Button>
                  </a>
                </div>
                <div className="relative">
                  {!iframeLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  )}
                  <iframe
                    src={adminDashboardUrl}
                    className="w-full h-[598px] border-0"
                    title="Chatwoot Admin Dashboard"
                    onLoad={() => setIframeLoaded(true)}
                    onError={() => setIframeLoaded(true)}
                    allow="camera; microphone; clipboard-write"
                    data-testid="chatwoot-admin-iframe"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendor Inboxes Tab */}
          <TabsContent value="vendors" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Vendor Inboxes</CardTitle>
                    <CardDescription>
                      {getProvisionedVendors().length} provisioned, {getUnprovisionedVendors().length} pending
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={provisionAllVendors} 
                    disabled={provisioningAll || getUnprovisionedVendors().length === 0}
                  >
                    {provisioningAll ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Provision All ({getUnprovisionedVendors().length})
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="mb-4">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search vendors..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Vendors Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left py-3 px-4 font-medium">Vendor</th>
                        <th className="text-left py-3 px-4 font-medium">Email</th>
                        <th className="text-center py-3 px-4 font-medium">Status</th>
                        <th className="text-center py-3 px-4 font-medium">Inbox ID</th>
                        <th className="text-center py-3 px-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors
                        .filter(v => 
                          !searchQuery || 
                          v.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          v.email?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map(vendor => (
                          <tr key={vendor.id} className="border-b hover:bg-muted/30">
                            <td className="py-3 px-4">
                              <div className="font-medium">{vendor.name || 'Unknown'}</div>
                              <div className="text-xs text-muted-foreground">
                                {vendor.vendor_profile?.store_name || 'No store name'}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-muted-foreground">{vendor.email}</td>
                            <td className="py-3 px-4 text-center">
                              {vendor.chatwoot?.provisioned ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Provisioned
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center font-mono text-xs">
                              {vendor.chatwoot?.inbox_id || '-'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {vendor.chatwoot?.inbox_id && (
                                <a 
                                  href={`${CHATWOOT_BASE_URL}/app/accounts/${CHATWOOT_ACCOUNT_ID}/inboxes/${vendor.chatwoot.inbox_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Button variant="ghost" size="sm">
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Leads Tab */}
          <TabsContent value="leads" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">All Leads Across Vendors</CardTitle>
                <CardDescription>
                  Global view of customer leads from all vendor inboxes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Lead Stage Summary */}
                <div className="grid grid-cols-5 gap-2 mb-6">
                  {LEAD_STAGES.map(stage => {
                    const StageIcon = stage.icon;
                    const count = stats?.leads_by_stage?.[stage.id] || 0;
                    return (
                      <div key={stage.id} className="text-center p-3 rounded-lg bg-muted/30">
                        <StageIcon className={`h-5 w-5 mx-auto mb-1 ${stage.color.split(' ')[1]}`} />
                        <div className="text-xl font-bold">{count}</div>
                        <div className="text-xs text-muted-foreground">{stage.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Leads Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left py-3 px-4 font-medium">Contact</th>
                        <th className="text-left py-3 px-4 font-medium">Vendor</th>
                        <th className="text-center py-3 px-4 font-medium">Stage</th>
                        <th className="text-center py-3 px-4 font-medium">Messages</th>
                        <th className="text-left py-3 px-4 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-12 text-center text-muted-foreground">
                            <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            No leads yet
                          </td>
                        </tr>
                      ) : (
                        leads.map(lead => {
                          const stage = LEAD_STAGES.find(s => s.id === lead.lead_stage) || LEAD_STAGES[0];
                          return (
                            <tr key={lead.id} className="border-b hover:bg-muted/30">
                              <td className="py-3 px-4">
                                <div className="font-medium">{lead.contact_name || 'Unknown'}</div>
                                <div className="text-xs text-muted-foreground">
                                  {lead.contact_phone || lead.contact_email}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-muted-foreground text-xs">
                                {lead.vendor_id?.slice(0, 8)}...
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge className={stage.color}>{stage.label}</Badge>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {lead.message_count || 0}
                              </td>
                              <td className="py-3 px-4 text-muted-foreground text-xs">
                                {new Date(lead.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* WABA Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Send className="h-5 w-5 text-green-600" />
                    WhatsApp Messaging
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Webhook Events</span>
                    <span className="font-bold">{stats?.total_webhook_events || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Provisioned Vendors</span>
                    <span className="font-bold">{stats?.provisioned_vendors || 0}</span>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">WABA Configuration</p>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span>Chatwoot URL</span>
                        <code className="bg-muted px-2 py-0.5 rounded">{CHATWOOT_BASE_URL}</code>
                      </div>
                      <div className="flex justify-between">
                        <span>Account ID</span>
                        <code className="bg-muted px-2 py-0.5 rounded">{CHATWOOT_ACCOUNT_ID}</code>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lead Funnel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    Lead Funnel
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {LEAD_STAGES.map((stage, index) => {
                    const count = stats?.leads_by_stage?.[stage.id] || 0;
                    const total = stats?.total_leads || 1;
                    const percentage = Math.round((count / total) * 100) || 0;
                    
                    return (
                      <div key={stage.id}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="flex items-center gap-2">
                            <stage.icon className="h-4 w-4" />
                            {stage.label}
                          </span>
                          <span className="font-medium">{count} ({percentage}%)</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <a href={adminDashboardUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full h-auto py-4 flex-col">
                        <MessageSquare className="h-6 w-6 mb-2" />
                        <span>Open Chatwoot</span>
                      </Button>
                    </a>
                    <Button 
                      variant="outline" 
                      className="w-full h-auto py-4 flex-col"
                      onClick={provisionAllVendors}
                      disabled={provisioningAll}
                    >
                      <Zap className="h-6 w-6 mb-2" />
                      <span>Provision All</span>
                    </Button>
                    <a href={`${CHATWOOT_BASE_URL}/app/accounts/${CHATWOOT_ACCOUNT_ID}/settings/inboxes`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full h-auto py-4 flex-col">
                        <Building2 className="h-6 w-6 mb-2" />
                        <span>Manage Inboxes</span>
                      </Button>
                    </a>
                    <a href={`${CHATWOOT_BASE_URL}/app/accounts/${CHATWOOT_ACCOUNT_ID}/reports`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full h-auto py-4 flex-col">
                        <BarChart3 className="h-6 w-6 mb-2" />
                        <span>View Reports</span>
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminCommunicationHub;
